import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, LayoutDashboard, Menu, X } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  {
    to: "/directory",
    label: "The Directory",
    test: "nav-directory",
    blurb: "Find real, wildly in love, diverse lovers from around the world.",
    image: "https://customer-assets.emergentagent.com/job_lovers-collective/artifacts/zpxdw6wp_Love%2C%20in%20Full%20%282%29.png",
  },
  {
    to: "/opportunities",
    label: "Opportunities",
    test: "nav-opportunities",
    blurb: "Find lovers who are the perfect fit for your upcoming creative endeavours.",
    image: "https://customer-assets.emergentagent.com/job_lovers-collective/artifacts/r3sw6vig_Love%2C%20in%20Full%20%283%29.png",
  },
  {
    to: "/about",
    label: "About Us",
    test: "nav-about",
    blurb: "The wedding industry has a representation problem, here's how we're fixing it.",
    image: null,
  },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  // Lock body scroll when menu open
  useEffect(() => {
    if (menuOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = original; };
    }
  }, [menuOpen]);

  // ESC to close
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [menuOpen]);

  const dashboardHref = user
    ? user.role === "admin" ? "/dashboard/admin"
    : user.role === "vendor" ? "/dashboard/vendor"
    : "/dashboard/lovers"
    : "";

  return (
    <>
      <header className="lif-header sticky top-0 z-40">
        {/* ===== DESKTOP — 3-column grid ===== */}
        <div className="hidden md:grid max-w-[1280px] mx-auto px-6 md:px-10 py-5 grid-cols-3 items-center gap-6">
          <div />
          <div className="flex flex-col items-center gap-2 col-span-1">
            <Link to="/" data-testid="logo-home-link" className="lif-wordmark text-[32px] leading-none">
              Love, in Full
            </Link>
            <nav className="flex items-center gap-7 text-sm whitespace-nowrap">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} data-testid={n.test}
                  className={({ isActive }) =>
                    `font-sans text-sm uppercase tracking-[0.15em] transition-opacity ${isActive ? "opacity-100" : "opacity-70 hover:opacity-100"}`
                  }
                  style={{ color: "#000" }}
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center justify-end gap-3">
            {user === undefined ? null : user ? (
              <>
                <NotificationBell />
                <button
                  data-testid="nav-dashboard-btn"
                  onClick={() => nav(dashboardHref)}
                  className="inline-flex items-center gap-2 text-sm font-medium text-black hover:text-wine transition"
                >
                  <LayoutDashboard className="w-4 h-4" strokeWidth={2} />
                  Dashboard
                </button>
                <button
                  data-testid="nav-logout-btn"
                  onClick={async () => { await logout(); nav("/"); }}
                  className="lif-btn-ghost text-sm"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4" strokeWidth={2} />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-black hover:text-wine transition">
                  Log in
                </Link>
                <Link to="/join" data-testid="nav-join" className="lif-btn-lilac !px-5 !py-2 text-sm">
                  Join
                </Link>
              </>
            )}
          </div>
        </div>

        {/* ===== MOBILE — stacked ===== */}
        <div className="md:hidden max-w-[1280px] mx-auto px-5 py-4">
          {/* Top row — hamburger on left, notifications/logout on right (logged-in only) */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="nav-mobile-menu-btn"
              aria-label="Open menu"
              className="w-10 h-10 inline-flex items-center justify-center rounded-full border-2 border-black bg-linen hover:bg-mustard transition"
            >
              <Menu className="w-5 h-5" strokeWidth={2}/>
            </button>

            <Link to="/" data-testid="logo-home-link-mobile" className="lif-wordmark text-[26px] leading-none">
              Love, in Full
            </Link>

            {user ? (
              <div className="flex items-center gap-2">
                <NotificationBell />
              </div>
            ) : (
              <span className="w-10 h-10" /> /* spacer for centering balance */
            )}
          </div>

          {/* Stacked CTAs row (only when logged out) */}
          {!user && user !== undefined && (
            <div className="flex items-center justify-center gap-3 mt-3">
              <Link to="/login" data-testid="nav-login-mobile" className="text-sm font-medium text-black hover:text-wine transition">
                Log in
              </Link>
              <span className="w-1 h-1 rounded-full bg-black/30" />
              <Link to="/join" data-testid="nav-join-mobile" className="lif-btn-lilac !px-5 !py-2 text-sm">
                Join
              </Link>
            </div>
          )}

          {user && (
            <div className="flex items-center justify-center gap-4 mt-3">
              <Link to={dashboardHref} data-testid="nav-dashboard-mobile" className="inline-flex items-center gap-2 text-sm font-medium text-black hover:text-wine transition">
                <LayoutDashboard className="w-4 h-4" strokeWidth={2}/>
                Dashboard
              </Link>
              <span className="w-1 h-1 rounded-full bg-black/30" />
              <button
                onClick={async () => { await logout(); nav("/"); }}
                data-testid="nav-logout-mobile"
                className="inline-flex items-center gap-2 text-sm font-medium text-black hover:text-wine transition"
              >
                <LogOut className="w-4 h-4" strokeWidth={2}/>
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ===== FULL-SCREEN MOBILE MENU ===== */}
      {menuOpen && (
        <div
          data-testid="nav-mobile-drawer"
          className="md:hidden fixed inset-0 z-[60] bg-linen overflow-y-auto"
          role="dialog"
          aria-modal="true"
        >
          <div className="px-6 py-5 flex items-center justify-between border-b-2 border-black/10">
            <span className="lif-wordmark text-[24px] leading-none">Love, in Full</span>
            <button
              onClick={() => setMenuOpen(false)}
              data-testid="nav-mobile-close-btn"
              aria-label="Close menu"
              className="w-10 h-10 inline-flex items-center justify-center rounded-full border-2 border-black bg-linen hover:bg-wine hover:text-white transition"
            >
              <X className="w-5 h-5" strokeWidth={2}/>
            </button>
          </div>

          <div className="px-6 py-8 space-y-10">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`${n.test}-mobile`}
                onClick={() => setMenuOpen(false)}
                className="block group"
              >
                <h2
                  className="text-[2.4rem] leading-none text-wine mb-3 transition-transform group-hover:translate-x-1"
                  style={{ fontFamily: '"Bringbold", Georgia, serif' }}
                >
                  {n.label}
                </h2>
                <p className="text-base text-black/85 mb-4 max-w-md leading-snug">{n.blurb}</p>
                {n.image && (
                  <div className="rounded-2xl overflow-hidden border-2 border-black aspect-[5/2]">
                    <img src={n.image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </Link>
            ))}

            {/* CTAs at bottom of drawer */}
            {!user && (
              <div className="pt-2 grid grid-cols-2 gap-3">
                <Link to="/login" onClick={() => setMenuOpen(false)} className="lif-btn-cream text-center justify-center">
                  Log in
                </Link>
                <Link to="/join" onClick={() => setMenuOpen(false)} className="lif-btn-lilac text-center justify-center">
                  Join
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
